'use client';

import { useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Slider } from './ui/slider';
import {
  FileText,
  Plus,
  Edit,
  Play,
  CheckCircle,
  XCircle,
  Clock,
  Search,
  Calendar,
  BarChart3,
  Pause,
  ChevronRight,
  ChevronDown,
  Trash2,
  Save,
  FolderTree,
} from 'lucide-react';
import { useTasksHierarchy } from '../hooks';
import { TaskHierarchyItem } from '../types';

export default function TasksTab() {
  const {
    hierarchy,
    loading: hierarchyLoading,
    error: hierarchyError,
    searchInHierarchy,
    getAllCategoriesFromHierarchy,
    flattenTasks,
    refreshHierarchy,
  } = useTasksHierarchy();

  const [selectedTask, setSelectedTask] = useState<TaskHierarchyItem | null>(
    null
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<
    Record<string, boolean>
  >({});
  const [editMode, setEditMode] = useState(false);
  const [editingRubric, setEditingRubric] = useState<any>(null);
  const [showNewTaskDialog, setShowNewTaskDialog] = useState(false);
  const [newTaskType, setNewTaskType] = useState<'task' | 'category'>('task');
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'draft',
    rubric: '',
    parent_id: null as string | null,
  });

  const allCategories = getAllCategoriesFromHierarchy(hierarchy);

  // Función para contar tasks recursivamente en una categoría
  const countTasksInCategory = (item: TaskHierarchyItem): number => {
    let count = 0;
    if (item.children) {
      item.children.forEach(child => {
        if (child.type === 'task') {
          count++;
        } else if (child.type === 'category') {
          count += countTasksInCategory(child);
        }
      });
    }
    return count;
  };

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, any> = {
      completed: {
        className: 'bg-green-100 text-green-800 border-green-200',
        icon: <CheckCircle className='w-3 h-3 mr-1' />,
      },
      failed: {
        className: 'bg-red-100 text-red-800 border-red-200',
        icon: <XCircle className='w-3 h-3 mr-1' />,
      },
      pending: {
        className: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        icon: <Clock className='w-3 h-3 mr-1' />,
      },
      active: {
        className: 'bg-blue-100 text-blue-800 border-blue-200',
        icon: <Play className='w-3 h-3 mr-1' />,
      },
      draft: {
        className: 'bg-gray-100 text-gray-800 border-gray-200',
        icon: <Edit className='w-3 h-3 mr-1' />,
      },
      paused: {
        className: 'bg-orange-100 text-orange-800 border-orange-200',
        icon: <Pause className='w-3 h-3 mr-1' />,
      },
      archived: {
        className: 'bg-purple-100 text-purple-800 border-purple-200',
        icon: <BarChart3 className='w-3 h-3 mr-1' />,
      },
    };

    const config = statusConfig[status] || statusConfig.draft;

    return (
      <Badge
        className={`flex items-center border text-xs px-2 py-0.5 ${config.className}`}
      >
        {status}
      </Badge>
    );
  };

  const handleOpenTask = (task: TaskHierarchyItem) => {
    setSelectedTask(task);
    setEditingRubric(task.rubric);
    setEditMode(false);
  };

  const handleNewTask = (parentId: string | null = null) => {
    setFormData({
      name: '',
      description: '',
      status: 'draft',
      rubric: '',
      parent_id: parentId,
    });
    setShowNewTaskDialog(true);
  };

  const createCategory = async (categoryData: {
    name: string;
    parent_id?: string | null;
    sort_order?: number;
  }) => {
    const response = await fetch('http://localhost:8000/api/v1/categories/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(categoryData),
    });

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ detail: 'Error desconocido' }));
      throw new Error(errorData.detail || 'Error al crear la categoría');
    }

    const result = await response.json();

    // Refrescar la jerarquía completa después de crear la categoría
    await refreshHierarchy();

    return result;
  };

  const createTask = async (taskData: {
    name: string;
    description: string;
    category_id?: string;
    status?: string;
    rubric?: string;
    enabled?: boolean;
  }) => {
    const response = await fetch('http://localhost:8000/api/v1/tasks', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(taskData),
    });

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ detail: 'Error desconocido' }));
      throw new Error(errorData.detail || 'Error al crear la tarea');
    }

    return response.json();
  };

  const handleCreateSubmit = async () => {
    setIsCreating(true);
    try {
      if (newTaskType === 'category') {
        await createCategory({
          name: formData.name,
          parent_id: formData.parent_id,
          sort_order: 0,
        });
      } else {
        await createTask({
          name: formData.name,
          description: formData.description,
          category_id: formData.parent_id || undefined,
          status: formData.status,
          rubric: formData.rubric || undefined,
          enabled: true,
        });
      }

      // Refresh the data and close modal
      setShowNewTaskDialog(false);
      setFormData({
        name: '',
        description: '',
        status: 'draft',
        rubric: '',
        parent_id: null,
      });
    } catch (error) {
      console.error('Error al crear:', error);
      // TODO: Mostrar mensaje de error al usuario
    } finally {
      setIsCreating(false);
    }
  };

  const renderTaskTree = (items: TaskHierarchyItem[], level = 0) => {
    return items.map(item => {
      if (item.type === 'category') {
        const isExpanded = expandedCategories[item.id] === true; // Por defecto cerrado

        return (
          <div key={item.id} className='mb-0.5'>
            <div
              className={`flex items-center py-1.5 px-2 rounded-md hover:bg-gray-50 cursor-pointer transition-colors`}
              style={{ paddingLeft: `${8 + level * 16}px` }}
            >
              <Button
                variant='ghost'
                size='sm'
                className='p-0 h-4 w-4 mr-2 hover:bg-transparent'
                onClick={e => {
                  e.stopPropagation();
                  toggleCategory(item.id);
                }}
              >
                {isExpanded ? (
                  <ChevronDown className='h-3 w-3 text-gray-500' />
                ) : (
                  <ChevronRight className='h-3 w-3 text-gray-500' />
                )}
              </Button>
              <FolderTree className='h-3.5 w-3.5 mr-2 text-blue-600 flex-shrink-0' />
              <span className='font-medium text-gray-800 text-sm truncate flex-1'>
                {item.name} ({countTasksInCategory(item)})
              </span>
              <Button
                variant='ghost'
                size='sm'
                className='h-6 w-6 p-0 opacity-0 group-hover:opacity-100 hover:bg-gray-200 flex-shrink-0'
                onClick={e => {
                  e.stopPropagation();
                  handleNewTask(item.id);
                }}
              >
                <Plus className='h-3 w-3' />
              </Button>
            </div>
            {isExpanded && (
              <div>
                {item.children && item.children.length > 0 ? (
                  renderTaskTree(item.children, level + 1)
                ) : (
                  <div
                    className='py-2 text-xs text-gray-400 italic'
                    style={{ paddingLeft: `${32 + level * 16}px` }}
                  >
                    No hay elementos
                  </div>
                )}
              </div>
            )}
          </div>
        );
      } else {
        // Es una task
        return (
          <div
            key={item.id}
            className='flex items-center py-1.5 px-2 rounded-md hover:bg-gray-50 cursor-pointer transition-colors group'
            style={{ paddingLeft: `${24 + level * 16}px` }}
            onClick={() => handleOpenTask(item)}
          >
            <FileText className='h-3.5 w-3.5 mr-2 text-gray-500 flex-shrink-0' />
            <span className='font-medium text-gray-800 text-sm truncate flex-1 min-w-0'>
              {item.name}
            </span>
            <div className='ml-2 flex-shrink-0'>
              {getStatusBadge(item.status || 'draft')}
            </div>
          </div>
        );
      }
    });
  };

  const filteredTasks = searchTerm
    ? searchInHierarchy(hierarchy, searchTerm)
    : flattenTasks(hierarchy);

  // Componente para visualizar y editar rúbricas
  const RubricEditor = ({
    rubric,
    isEditing,
    onChange,
  }: {
    rubric: any;
    isEditing: boolean;
    onChange: (rubric: any) => void;
  }) => {
    console.log('🎯 RubricEditor recibió:', {
      rubric,
      hasRubric: !!rubric,
      hasCriterios: !!rubric?.criterios,
      criteriosLength: rubric?.criterios?.length || 0,
    });

    if (
      !rubric ||
      !rubric.criterios ||
      !Array.isArray(rubric.criterios) ||
      rubric.criterios.length === 0
    ) {
      return (
        <div className='text-center py-8 text-gray-500'>
          <p>No hay criterios de evaluación definidos.</p>
          {isEditing && (
            <Button
              onClick={() =>
                onChange({
                  criterios: [
                    {
                      nombre: 'Nuevo criterio',
                      peso: 100,
                      descripcion: 'Descripción',
                    },
                  ],
                })
              }
              className='mt-2'
            >
              Crear criterios
            </Button>
          )}
        </div>
      );
    }

    const handleCriterionChange = (
      index: number,
      field: string,
      value: any
    ) => {
      if (!isEditing) return;

      const updatedRubric = { ...rubric };
      updatedRubric.criterios[index][field] =
        field === 'peso' ? Number.parseInt(value) : value;
      onChange(updatedRubric);
    };

    const addCriterion = () => {
      const updatedRubric = { ...rubric };
      updatedRubric.criterios.push({
        nombre: 'Nuevo criterio',
        peso: 10,
        descripcion: 'Descripción del nuevo criterio',
      });
      onChange(updatedRubric);
    };

    const removeCriterion = (index: number) => {
      const updatedRubric = { ...rubric };
      updatedRubric.criterios.splice(index, 1);
      onChange(updatedRubric);
    };

    return (
      <div className='space-y-4'>
        <div className='flex items-center justify-between'>
          <h3 className='text-lg font-semibold text-gray-900'>
            Criterios de Evaluación
          </h3>
          {isEditing && (
            <Button size='sm' variant='outline' onClick={addCriterion}>
              <Plus className='h-4 w-4 mr-1' /> Añadir Criterio
            </Button>
          )}
        </div>

        <div className='border rounded-lg overflow-hidden'>
          <Table>
            <TableHeader>
              <TableRow className='bg-gray-50'>
                <TableHead className='w-1/3'>Criterio</TableHead>
                <TableHead className='w-1/6'>Peso (%)</TableHead>
                <TableHead className='w-1/2'>Descripción</TableHead>
                {isEditing && <TableHead className='w-12'></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rubric.criterios.map((criterio: any, index: number) => (
                <TableRow key={index}>
                  <TableCell>
                    {isEditing ? (
                      <Input
                        value={criterio.nombre}
                        onChange={e =>
                          handleCriterionChange(index, 'nombre', e.target.value)
                        }
                      />
                    ) : (
                      <div className='font-medium'>{criterio.nombre}</div>
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <div className='flex items-center space-x-2'>
                        <Slider
                          value={[criterio.peso]}
                          min={0}
                          max={100}
                          step={5}
                          onValueChange={value =>
                            handleCriterionChange(index, 'peso', value[0])
                          }
                          className='w-24'
                        />
                        <span className='text-sm font-medium w-8'>
                          {criterio.peso}%
                        </span>
                      </div>
                    ) : (
                      <div className='font-medium'>{criterio.peso}%</div>
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <Input
                        value={criterio.descripcion}
                        onChange={e =>
                          handleCriterionChange(
                            index,
                            'descripcion',
                            e.target.value
                          )
                        }
                      />
                    ) : (
                      <div>{criterio.descripcion}</div>
                    )}
                  </TableCell>
                  {isEditing && (
                    <TableCell>
                      <Button
                        variant='ghost'
                        size='sm'
                        className='h-8 w-8 p-0 text-red-600'
                        onClick={() => removeCriterion(index)}
                      >
                        <Trash2 className='h-4 w-4' />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Visualización del YAML generado (solo en modo edición) */}
        {isEditing && (
          <div className='mt-4'>
            <Label className='text-sm font-medium'>YAML generado:</Label>
            <Textarea
              value={`criterios:\n${rubric.criterios.map((c: any) => `  - nombre: "${c.nombre}"\n    peso: ${c.peso}\n    descripcion: "${c.descripcion}"`).join('\n')}`}
              className='font-mono text-xs mt-1'
              rows={6}
              readOnly
            />
          </div>
        )}
      </div>
    );
  };

  if (hierarchyLoading) {
    return (
      <div className='flex items-center justify-center h-full'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4'></div>
          <p className='text-gray-600'>Cargando tasks...</p>
        </div>
      </div>
    );
  }

  if (hierarchyError) {
    return (
      <div className='flex items-center justify-center h-full'>
        <div className='text-center'>
          <div className='text-red-600 mb-4'>Error al cargar las tasks</div>
          <p className='text-gray-600'>{hierarchyError.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <div className='grid grid-cols-12 gap-6'>
        {/* Panel izquierdo - Árbol de categorías y tasks */}
        <div className='col-span-4 bg-white rounded-lg border shadow-sm min-w-0'>
          <div className='p-4 border-b'>
            <div className='flex items-center justify-between mb-4'>
              <h3 className='font-semibold text-gray-900'>
                Estructura de Tasks
              </h3>
              <Button
                size='sm'
                className='bg-blue-600 hover:bg-blue-700 text-white flex-shrink-0'
                onClick={() => handleNewTask()}
              >
                <Plus className='h-4 w-4 mr-1' />
                Nueva
              </Button>
            </div>
            <div className='relative'>
              <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4' />
              <Input
                placeholder='Buscar tasks...'
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className='pl-10'
              />
            </div>
          </div>
          <div className='max-h-[calc(100vh-320px)] overflow-y-auto overflow-x-hidden'>
            <div className='p-2'>
              {searchTerm ? (
                // Resultados de búsqueda
                filteredTasks.length > 0 ? (
                  filteredTasks.map(task => (
                    <div
                      key={task.id}
                      className='flex items-center py-1.5 px-2 rounded-md hover:bg-gray-50 cursor-pointer transition-colors group'
                      onClick={() => {
                        // Convertir MCPTask a TaskHierarchyItem para compatibilidad
                        const hierarchyItem: TaskHierarchyItem = {
                          id: task.id,
                          name: task.name,
                          type: 'task',
                          description: task.description,
                          status: task.status,
                          createdAt: task.createdAt,
                          lastRun: task.lastRun,
                          executions: task.executions,
                          successRate: task.successRate,
                          rubric: task.rubric ? JSON.parse(task.rubric) : null,
                          children: [],
                        };
                        handleOpenTask(hierarchyItem);
                      }}
                    >
                      <FileText className='h-3.5 w-3.5 mr-2 text-gray-500 flex-shrink-0' />
                      <span className='font-medium text-gray-800 text-sm truncate flex-1 min-w-0'>
                        {task.name}
                      </span>
                      <div className='ml-2 flex-shrink-0'>
                        {getStatusBadge(task.status)}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className='py-4 text-center text-gray-500'>
                    No se encontraron resultados
                  </div>
                )
              ) : (
                // Árbol jerárquico
                renderTaskTree(hierarchy)
              )}
            </div>
          </div>
        </div>

        {/* Panel derecho - Detalles de la task seleccionada */}
        <div className='col-span-8'>
          {selectedTask ? (
            <Card className='border-0 shadow-sm h-full'>
              <CardHeader className='border-b bg-white'>
                <div className='flex items-center justify-between'>
                  <div className='flex items-center space-x-3'>
                    <div className='flex items-center justify-center w-10 h-10 bg-green-100 rounded-lg'>
                      <FileText className='h-5 w-5 text-green-600' />
                    </div>
                    <div>
                      <CardTitle className='text-lg text-gray-900'>
                        {selectedTask.name}
                      </CardTitle>
                      <CardDescription>
                        {selectedTask.description}
                      </CardDescription>
                    </div>
                  </div>
                  <div className='flex items-center space-x-2'>
                    {editMode ? (
                      <>
                        <Button
                          className='bg-green-600 hover:bg-green-700 text-white'
                          onClick={() => setEditMode(false)}
                        >
                          <Save className='h-4 w-4 mr-1' />
                          Guardar
                        </Button>
                        <Button
                          variant='outline'
                          onClick={() => {
                            setEditMode(false);
                            setEditingRubric(selectedTask.rubric);
                          }}
                        >
                          Cancelar
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant='outline'
                          onClick={() => setEditMode(true)}
                        >
                          <Edit className='h-4 w-4 mr-1' />
                          Editar
                        </Button>
                        <Button className='bg-blue-600 hover:bg-blue-700 text-white'>
                          <Play className='h-4 w-4 mr-1' />
                          Ejecutar
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className='p-6'>
                <div className='space-y-6'>
                  {/* Información general */}
                  <div className='grid grid-cols-3 gap-4'>
                    <div className='bg-gray-50 p-4 rounded-lg border border-gray-100'>
                      <div className='text-sm text-gray-600 mb-1'>Estado</div>
                      <div>
                        {getStatusBadge(selectedTask.status || 'draft')}
                      </div>
                    </div>
                    <div className='bg-gray-50 p-4 rounded-lg border border-gray-100'>
                      <div className='text-sm text-gray-600 mb-1'>
                        Ejecuciones
                      </div>
                      <div className='text-lg font-semibold text-gray-900'>
                        {selectedTask.executions || 0}
                      </div>
                    </div>
                    <div className='bg-gray-50 p-4 rounded-lg border border-gray-100'>
                      <div className='text-sm text-gray-600 mb-1'>
                        Tasa de Éxito
                      </div>
                      <div className='flex items-center space-x-2'>
                        <div className='text-lg font-semibold text-green-600'>
                          {selectedTask.successRate || 0}%
                        </div>
                        <div className='w-16 h-2 bg-gray-200 rounded-full overflow-hidden'>
                          <div
                            className='h-full bg-green-500 rounded-full'
                            style={{
                              width: `${selectedTask.successRate || 0}%`,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Fechas */}
                  <div className='grid grid-cols-2 gap-4'>
                    <div className='flex items-center space-x-2 text-sm text-gray-600'>
                      <Calendar className='h-4 w-4' />
                      <span>
                        Creado:{' '}
                        {selectedTask.createdAt
                          ? new Date(
                              selectedTask.createdAt
                            ).toLocaleDateString()
                          : 'N/A'}
                      </span>
                    </div>
                    <div className='flex items-center space-x-2 text-sm text-gray-600'>
                      <Clock className='h-4 w-4' />
                      <span>
                        Última ejecución:{' '}
                        {selectedTask.lastRun
                          ? new Date(selectedTask.lastRun).toLocaleString()
                          : 'Nunca'}
                      </span>
                    </div>
                  </div>

                  {/* Editor de rúbricas */}
                  <RubricEditor
                    rubric={editingRubric}
                    isEditing={editMode}
                    onChange={setEditingRubric}
                  />
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className='flex items-center justify-center h-full bg-white rounded-lg border shadow-sm p-8'>
              <div className='text-center'>
                <div className='flex justify-center mb-4'>
                  <div className='flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full'>
                    <FileText className='h-8 w-8 text-blue-600' />
                  </div>
                </div>
                <h3 className='text-lg font-semibold text-gray-900 mb-2'>
                  Selecciona una Task
                </h3>
                <p className='text-gray-600 max-w-md'>
                  Selecciona una task del panel izquierdo para ver sus detalles
                  y editar su rúbrica de evaluación.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal para crear nueva task o categoría */}
      <Dialog open={showNewTaskDialog} onOpenChange={setShowNewTaskDialog}>
        <DialogContent className='max-w-2xl'>
          <DialogHeader>
            <DialogTitle>
              {newTaskType === 'task'
                ? 'Crear Nueva Task'
                : 'Crear Nueva Categoría'}
            </DialogTitle>
            <DialogDescription>
              {newTaskType === 'task'
                ? 'Define una nueva tarea con su rúbrica de evaluación'
                : 'Crea una nueva categoría para organizar tus tasks'}
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-4'>
            <div className='flex space-x-4 mb-2'>
              <Button
                variant={newTaskType === 'task' ? 'default' : 'outline'}
                className={
                  newTaskType === 'task' ? 'bg-blue-600 hover:bg-blue-700' : ''
                }
                onClick={() => setNewTaskType('task')}
              >
                <FileText className='h-4 w-4 mr-2' />
                Task
              </Button>
              <Button
                variant={newTaskType === 'category' ? 'default' : 'outline'}
                className={
                  newTaskType === 'category'
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : ''
                }
                onClick={() => setNewTaskType('category')}
              >
                <FolderTree className='h-4 w-4 mr-2' />
                Categoría
              </Button>
            </div>

            <div>
              <Label htmlFor='parent-category'>Categoría Padre</Label>
              <Select
                value={formData.parent_id || 'root'}
                onValueChange={value =>
                  setFormData(prev => ({
                    ...prev,
                    parent_id: value === 'root' ? null : value,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder='Seleccionar categoría padre (opcional)' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='root'>
                    Raíz (Sin categoría padre)
                  </SelectItem>
                  {allCategories.map(category => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.fullPath}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor='task-name'>Nombre</Label>
              <Input
                id='task-name'
                placeholder={`Nombre de la ${newTaskType === 'task' ? 'task' : 'categoría'}`}
                value={formData.name}
                onChange={e =>
                  setFormData(prev => ({ ...prev, name: e.target.value }))
                }
              />
            </div>

            {newTaskType === 'task' && (
              <>
                <div>
                  <Label htmlFor='task-desc'>Descripción</Label>
                  <Input
                    id='task-desc'
                    placeholder='Descripción breve de la tarea'
                    value={formData.description}
                    onChange={e =>
                      setFormData(prev => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                  />
                </div>
                <div>
                  <Label htmlFor='task-status'>Estado</Label>
                  <Select
                    value={formData.status}
                    onValueChange={value =>
                      setFormData(prev => ({ ...prev, status: value }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='draft'>Borrador</SelectItem>
                      <SelectItem value='active'>Activa</SelectItem>
                      <SelectItem value='paused'>Pausada</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor='task-rubric'>Rúbrica Inicial</Label>
                  <Textarea
                    id='task-rubric'
                    placeholder="# Rúbrica&#10;criterios:&#10;  - nombre: 'Criterio 1'&#10;    peso: 50&#10;    descripcion: 'Descripción del criterio'"
                    className='font-mono text-sm'
                    rows={6}
                    value={formData.rubric}
                    onChange={e =>
                      setFormData(prev => ({ ...prev, rubric: e.target.value }))
                    }
                  />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setShowNewTaskDialog(false)}
              disabled={isCreating}
            >
              Cancelar
            </Button>
            <Button
              className='bg-blue-600 hover:bg-blue-700'
              onClick={handleCreateSubmit}
              disabled={isCreating || !formData.name.trim()}
            >
              {isCreating
                ? 'Creando...'
                : newTaskType === 'task'
                  ? 'Crear Task'
                  : 'Crear Categoría'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
